import React, { useContext } from 'react';
import { invokeMap } from 'lodash';
import {
  getPatientCareTeamUrl,
  getPatientBookingsUrl,
  UserRoles,
  useRequest,
  GetPatientCareTeamUrlDto,
  getCachedPatientPrescriptionsUrl,
  hasNoCareTeam,
  GetPatientBookingsUrlDto,
  getReservedBookingsWithin48Hours,
  BookingDto,
  getPatientLegacyCommunityOnlyUrl,
} from '@monument/core';
import { Result } from 'ahooks/lib/useRequest/src/types';

interface Value {
  run: () => Promise<any>;
  loadingClinicians: boolean;
  clinicians: Result<GetPatientCareTeamUrlDto, []>['data'];
  hasNoCareTeam: boolean;
  therapist: GetPatientCareTeamUrlDto[0] | undefined;
  physician: GetPatientCareTeamUrlDto[0] | undefined;
  refreshClinicians: Result<GetPatientCareTeamUrlDto, []>['refreshAsync'];
  loadingMedications: boolean;
  medications: any;
  loadingBookings: boolean;
  bookings: Result<GetPatientBookingsUrlDto, []>['data'];
  reservedBookingsIn48Hours: BookingDto[];
  refreshBookings: Result<GetPatientBookingsUrlDto, []>['refreshAsync'];
  /**
   * Whether the patient used to be a community only member, and never
   * sought clinical care.
   * These patients should not be prompted to complete intake until they
   * book an appointment
   */
  legacyCommunityOnly: Result<boolean, []>['data'];
}

const MembershipContext = React.createContext<Value>({} as Value);
export const useMembership = () => useContext(MembershipContext);

export const MembershipContextProvider = ({ children }) => {
  const careTeam = useRequest<GetPatientCareTeamUrlDto, []>(getPatientCareTeamUrl(), {
    manual: true,
  });
  const therapist = careTeam.data?.find(clinician => clinician.role === UserRoles.Therapist);
  const physician = careTeam.data?.find(clinician => clinician.role === UserRoles.Doctor);
  // Dosespot outages are a little more frequent than for other services
  // Default to cached data for now
  const prescriptions = useRequest(getCachedPatientPrescriptionsUrl(), {
    manual: true,
  });
  const bookings = useRequest<GetPatientBookingsUrlDto, []>(getPatientBookingsUrl(), {
    manual: true,
  });
  const legacyCommunityOnly = useRequest<boolean>(getPatientLegacyCommunityOnlyUrl(), {
    manual: true,
  });
  const allRequests = [careTeam, prescriptions, bookings, legacyCommunityOnly];

  return (
    <MembershipContext.Provider
      value={{
        run: () => Promise.all(invokeMap(allRequests, 'runAsync')),
        loadingClinicians: careTeam.loading,
        clinicians: careTeam.data,
        hasNoCareTeam: hasNoCareTeam(careTeam.data),
        therapist,
        physician,
        refreshClinicians: careTeam.refreshAsync,
        loadingMedications: prescriptions.loading,
        medications: prescriptions.data,
        loadingBookings: bookings.loading,
        bookings: bookings.data,
        reservedBookingsIn48Hours: getReservedBookingsWithin48Hours(bookings.data),
        refreshBookings: async () => {
          const [result] = await Promise.all([
            bookings.refreshAsync(),
            // Value depends on existence of bookings
            legacyCommunityOnly.refreshAsync(),
          ]);
          return result;
        },
        legacyCommunityOnly: legacyCommunityOnly.data,
      }}
    >
      {children}
    </MembershipContext.Provider>
  );
};
